/**
 * Pure data provider for the `timeline[column].event` renderer plugin
 * (timed events painted along a calendar row's column time axis).
 *
 * Walks the row tree starting from a renderer / alias root, finds the
 * nested `calendar-list` aggregate, and returns the timed (non-all-day)
 * events overlapping a date range. The **all-day counterpart** is
 * `calendar-allday-events.ts` (`timeline[row].event`) — same walker,
 * opposite filter. Together they ensure no event is duplicated across
 * the all-day strip and the timed grid.
 *
 * Both DOM and CLI adapters consume this so `timeline[column]`
 * fixed-child intent lines up across surfaces.
 */

import { NaiveDate } from "@tento-chrono";

import type { CalendarEvent } from "../core/dtype.ts";
import { CalendarListEventsProvider } from "./calendar-list-events.ts";
import type {
  FixedChildIntent,
  PluginDataProviders,
  TimelineItemProvider,
} from "./plugin-providers.ts";
import type { ModelRow } from "../rows/model-row.ts";

const PLUGIN_ID = "timeline[column].event";

/**
 * The data provider behind the timed event renderer (`timeline[column].event`): the timed events
 * under a row within a range.
 */
export class CalendarTimedEventsProvider
  implements TimelineItemProvider<CalendarEvent>
{
  readonly pluginId: "timeline[column].event" = PLUGIN_ID;

  // Reuse the calendar-list walker — same row tree, same aggregate
  // facade. The only difference is that timed events filter to
  // non-full-day items.
  #inner = new CalendarListEventsProvider();

  // ── TimelineItemProvider ───────────────────────────────────────────

  items(row: ModelRow, range: NaiveDate.Range): readonly CalendarEvent[] {
    return this.#inner
      .eventsInRange(row, range)
      .filter((event) => !this.#inner.isAllDay(event));
  }

  dateRange(event: CalendarEvent): NaiveDate.Range {
    return this.#inner.eventDateRange(event);
  }

  itemId(event: CalendarEvent): string {
    return event.id;
  }

  // ── Pure FixedChildIntent producer ────────────────────────────────

  fixedChildren(
    row: ModelRow,
    range: NaiveDate.Range,
  ): readonly FixedChildIntent[] {
    return this.items(row, range).map((event) => ({
      id: event.id,
      range: this.dateRange(event),
      align: "center",
      fillWidth: false,
      isOverflow: false,
      pluginId: PLUGIN_ID,
    }));
  }
}

/** `dataProvider` factory for the catalog. */
export function calendarTimedEventsDataProvider(): PluginDataProviders<
  CalendarEvent,
  CalendarEvent
> {
  const provider = new CalendarTimedEventsProvider();
  return {
    timelineItems: provider,
    fixedChildren: (row, range) => provider.fixedChildren(row, range),
  };
}
